import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  DailyLoginResult,
  Nullable,
  UserWithToken,
  WithData,
  XPLeaderboardEntry,
  XPSource,
  XPSummary,
  XPTransaction,
} from '@ygoargentina/sdk/api/api.types';

const XP_SUMMARY_FIELDS = gql`
  fragment XPSummaryFields on XPSummary {
    userId
    totalXp
    level
    xpIntoLevel
    xpForNextLevel
    currentStreak
    longestStreak
    lastActivityAt
  }
`;

const XP_TRANSACTION_FIELDS = gql`
  fragment XPTransactionFields on XPTransaction {
    id
    userId
    amount
    source
    referenceId
    createdAt
  }
`;

export const getMyXPSummary = async (user: UserWithToken) =>
  getGraphQLQueryClient(user).request<WithData<XPSummary>>(
    gql`
      ${XP_SUMMARY_FIELDS}
      query myXPSummary {
        data: myXPSummary {
          ...XPSummaryFields
        }
      }
    `
  );

export const getUserXPSummary = async (
  user: Nullable<UserWithToken>,
  userId: number
) =>
  getGraphQLQueryClient(user).request<WithData<XPSummary>>(
    gql`
      ${XP_SUMMARY_FIELDS}
      query userXPSummary($userId: Int!) {
        data: userXPSummary(userId: $userId) {
          ...XPSummaryFields
        }
      }
    `,
    { userId }
  );

export const getMyXPTransactions = async (
  user: UserWithToken,
  limit?: number
) =>
  getGraphQLQueryClient(user).request<WithData<XPTransaction[]>>(
    gql`
      ${XP_TRANSACTION_FIELDS}
      query myXPTransactions($limit: Int) {
        data: myXPTransactions(limit: $limit) {
          ...XPTransactionFields
        }
      }
    `,
    { limit: limit ?? null }
  );

export const getXPConfig = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<
    WithData<Array<{ source: XPSource; amount: number }>>
  >(
    gql`
      query xpConfig {
        data: xpConfig {
          source
          amount
        }
      }
    `
  );

export type LeaderboardPeriod = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ALL_TIME';

export const getXPLeaderboard = async (
  user: Nullable<UserWithToken>,
  limit?: number,
  period?: LeaderboardPeriod
) =>
  getGraphQLQueryClient(user).request<WithData<XPLeaderboardEntry[]>>(
    gql`
      query xpLeaderboard($limit: Int, $period: LeaderboardPeriod) {
        data: xpLeaderboard(limit: $limit, period: $period) {
          userId
          totalXp
          level
          periodXp
          user {
            id
            name
            lastName
          }
        }
      }
    `,
    { limit: limit ?? null, period: period ?? null }
  );

export const claimDailyLogin = async (user: UserWithToken) =>
  getGraphQLQueryClient(user).request<WithData<DailyLoginResult>>(
    gql`
      mutation claimDailyLogin {
        data: claimDailyLogin {
          awarded
          totalXp
          previousLevel
          newLevel
          leveledUp
          alreadyClaimedToday
        }
      }
    `
  );
